import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import { useEffect, useState } from 'react';

import { getPatrollers } from '../../api.js';
import { useAuth } from '../../AuthContext';

/**
 * Pick several patrollers by name. Loads the patrol's patrollers once when opened.
 * Patrollers in `excludeIds` (already granted) are not offered.
 */
export const AddPatrollersDialog = ({ title, excludeIds = [], onAdd, onClose }) => {
    const { token } = useAuth();

    const [ patrollers, setPatrollers ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState('');
    const [ search, setSearch ] = useState('');
    const [ selected, setSelected ] = useState([]);
    const [ saving, setSaving ] = useState(false);

    // One fetch when the dialog opens. No retry on failure.
    useEffect(() => {
        let active = true;

        getPatrollers(token).then(result => {
            if (!active) return;

            if (result?.error) {
                setError(result.error);
            } else {
                // /manage/patrollers carries a disabled flag; never offer disabled users
                setPatrollers((result || []).filter(p => !p.disabled));
            }
            setLoading(false);
        });

        return () => { active = false; };
    }, []);

    const needle = search.trim().toLowerCase();
    const visible = patrollers
        .filter(p => !excludeIds.includes(p.id))
        .filter(p => !needle || `${ p.firstName || '' } ${ p.lastName || '' }`.toLowerCase().includes(needle));

    const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [ ...s, id ]));

    const handleAdd = async () => {
        if (saving || selected.length === 0) return;

        setSaving(true);
        setError('');

        const result = await onAdd(selected);

        if (result?.error) {
            const names = (result.userIds || []).map(id => {
                const p = patrollers.find(x => x.id === id);

                return p ? `${ p.firstName || '' } ${ p.lastName || '' }`.trim() : id;
            });

            setError(names.length ? `Not active members of this patrol: ${ names.join(', ') }.` : result.error);
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={ onClose } fullWidth maxWidth="xs">
            <DialogTitle>{ title }</DialogTitle>

            <DialogContent>
                { error && <Alert severity="error" sx={ { mb: 1 } }>{ error }</Alert> }

                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search patrollers by name..."
                    value={ search }
                    onChange={ (e) => setSearch(e.target.value) }
                    sx={ { my: 1 } }
                />

                { loading && <div>Loading...</div> }

                { visible.map(p => (
                    <div key={ p.id } data-testid={ `pick-${ p.id }` }>
                        <FormControlLabel
                            control={ <Checkbox size="small" checked={ selected.includes(p.id) } onChange={ () => toggle(p.id) } /> }
                            label={ `${ p.firstName || '' } ${ p.lastName || '' }`.trim() || p.id }
                        />
                    </div>
                )) }
            </DialogContent>

            <DialogActions>
                <Button onClick={ onClose } color="secondary">cancel</Button>
                <Button onClick={ handleAdd } variant="contained" disabled={ saving || selected.length === 0 }>
                    { selected.length > 0 ? `Add ${ selected.length }` : 'Add' }
                </Button>
            </DialogActions>
        </Dialog>
    );
};
